import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  Divider,
  IconButton,
  Typography,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { useIsMobile } from "@/hooks/useIsMobile";
import { V2_COLORS } from "../constants/colors";

interface BaseModalProps {
  open: boolean;
  title: string;
  loading?: boolean;
  confirmLabel: string;
  cancelLabel?: string;
  confirmDisabled?: boolean;
  confirmColor?: string;
  confirmIcon?: React.ReactNode;
  onClose: () => void;
  onConfirm: () => void;
  children: React.ReactNode;
}

export default function BaseModal({
  open,
  title,
  loading,
  confirmLabel,
  cancelLabel = "Cancel",
  confirmDisabled,
  confirmColor,
  confirmIcon,
  onClose,
  onConfirm,
  children,
}: BaseModalProps) {
  const isMobile = useIsMobile();
  const phoneAction = isMobile ? { flexGrow: 1, flexBasis: 0, px: 1, whiteSpace: "nowrap" } : undefined;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: "4px", maxWidth: 560, width: "100%", ...(isMobile && { margin: 2, maxWidth: "min(560px, calc(100% - 32px))" }) } }}
    >
      {/* Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" px={2} py={2}>
        <Typography fontWeight={700} fontSize="16px" lineHeight="24px">
          {title}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close sx={{ fontSize: 24, color: V2_COLORS.textSecondary }} />
        </IconButton>
      </Box>

      <Divider />

      {/* Content */}
      <Box px={3} py={3}>
        {children}
      </Box>

      {/* Footer */}
      <Box display="flex" gap={1.5} justifyContent="flex-end" px={3} pb={2}>
        <Button
          onClick={onClose}
          disabled={loading}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            height: 36,
            px: 2,
            color: "#666",
            backgroundColor: "#E6E6E6",
            "&:hover": { backgroundColor: "#D6D6D6" },
            boxShadow: "none",
            ...phoneAction,
          }}
        >
          {cancelLabel}
        </Button>
        <Button
          variant="contained"
          disabled={confirmDisabled || loading}
          onClick={onConfirm}
          startIcon={loading ? <CircularProgress size={14} /> : confirmIcon}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: "14px",
            height: 36,
            px: 2,
            boxShadow: "none",
            backgroundColor: confirmColor || V2_COLORS.primary,
            "&:hover": { backgroundColor: confirmColor || V2_COLORS.primary },
            ...phoneAction,
          }}
        >
          {confirmLabel}
        </Button>
      </Box>
    </Dialog>
  );
}
